<template>
  <v-card-actions>
    <v-badge :value="VMCount" :content="VMCount"
      class="voicemail">
      <v-tooltip bottom light>
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" :disabled="!canShowVMCount">mdi-voicemail</v-icon>
        </template>
        <span>{{ $t('buddy.tooltip.vm') }}</span>
      </v-tooltip>
    </v-badge>
    <v-spacer></v-spacer>

    <v-tooltip bottom light v-if="canChanSpy">
      <template v-slot:activator="{ on }">
        <v-menu v-on="on" bottom :disabled="chanSpyDisabled">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" class="chanspy" :disabled="chanSpyDisabled">
              <v-icon>mdi-headphones</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item v-for="(call, idx) in buddy.calls" :key="idx" @click="bargeinPrompt(call)">
              <v-list-item-title class="chanspy-menu">
                <v-icon small>mdi-headphones</v-icon>
                {{ buddy.exten }}
                <v-icon>mdi-arrow-{{ call.isIncoming ? 'left' : 'right'}}</v-icon>
                {{call.user}}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <span>{{ $t('buddy.tooltip.listen') }}</span>
    </v-tooltip>

    <v-tooltip bottom light>
      <template v-slot:activator="{ on }">
        <v-btn icon v-on="on" class="sipcall"
          :disabled="sipcallBtnDisabled"
          @click="!showTelURLs ? callBuddy() : null"
          :href="showTelURLs ? `tel:${buddyExten()}` : null"
        >
          <v-icon color="primary">mdi-phone</v-icon>
        </v-btn>
      </template>
      <span>{{ tooltipCallSIP }}</span>
    </v-tooltip>

    <v-badge v-if="canChatsRead && !embedded" :value="chatUnread" :content="chatUnread"
      data-testid="chat-unread-badge" overlap>
      <v-tooltip bottom light>
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on" data-testid="start-chat-btn"
            v-if="canChatsRead && !embedded" :loading="startChatLoading" @click="startChat()">
            <v-icon data-testid="start-chat-btn-icon" color="primary">mdi-message-text</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('buddy.tooltip.startChat') }}</span>
      </v-tooltip>
    </v-badge>

    <v-spacer></v-spacer>
    <v-tooltip bottom light>
      <template v-slot:activator="{ on }">
        <v-btn icon small @click="stopWatchBuddy(buddy)"
          v-on="on" class="unwatch">
          <v-icon small color="warning">mdi-eye-off</v-icon>
        </v-btn>
      </template>
      <span>{{ $t('buddy.tooltip.unwatch') }}</span>
    </v-tooltip>
    <BargeinForm
      @cancel="showBargeinPrompt=false"
      :show="showBargeinPrompt"
      :owner="buddy"
      :call="bargeinCall"/>
  </v-card-actions>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BargeinForm from './bargein_form'
import MixinChatRestApi from '@/mixins/chat/rest'
import EmbeddedLocalStorage from '@/api/embedded'

export default {
  name: 'actions-panel',
  data () {
    return {
      showBargeinPrompt: false,
      bargeinCall: {},
      startChatLoading: false
    }
  },
  props: {
    buddy: {
      type: Object,
      required: true
    }
  },
  components: { BargeinForm },
  watch: {
    isOnCall: function (val) {
      if (val === false) {
        this.showBargeinPrompt = false
      }
    }
  },
  mixins: [MixinChatRestApi],
  computed: {
    ...mapGetters({
      sipuaExten: 'sipuaExten',
      permChanSpy: 'permissions/canChanSpy',
      showVMCount: 'permissions/showVMCount',
      canChatsRead: 'permissions/chatsRead',
      currentUser: 'chat/authenticatedRocketChatUser',
      directConversations: 'chat/directConversations',
      showTelURLs: 'showTelURLs',
      dmLoaded: 'chat/dmLoaded'
    }),
    sipcallBtnDisabled: function () {
      return this.buddy?.exten === this.sipuaExten
    },
    canChanSpy: function () {
      return this.permChanSpy(this.buddy?.id)
    },
    hasParkedCalls: function () {
      return this.buddy.calls.some(c => c.isParked)
    },
    buddyChatUsername: function () {
      return this.buddy?.chatUsername
    },
    parkedSlot: function () {
      const call = this.buddy.calls.find(c => c.isParked)
      if (call) {
        return call.user
      }
      return ''
    },
    tooltipCallSIP: function () {
      if (this.hasParkedCalls) {
        return this.$t('buddy.tooltip.unpark')
      }
      return this.$t('buddy.tooltip.sipcall')
    },
    isOnCall: function () {
      return !!this.buddy.isOnCall
    },
    chanSpyDisabled: function () {
      return !this.isOnCall || this.hasParkedCalls
    },
    canShowVMCount: function () {
      return this.showVMCount(this.buddy?.id)
    },
    VMCount: function () {
      if (!this.showVMCount(this.buddy?.id)) {
        return 0
      }
      return this.buddy?.vmnum
    },
    chatUnread: function () {
      const dm = this.directConversations.find(dm => dm?.to_user?.chat_username === this.buddyChatUsername)
      return dm?.unread || 0
    },
    embedded () {
      return EmbeddedLocalStorage.get()
    }
  },
  methods: {
    ...mapActions({
      stopWatchBuddy: 'users/stopWatchBuddy',
      callNumber: 'callNumber',
      chatAuth: 'chat/auth',
      getDirectMessages: 'chat/getDirectMessages',
      getMoreDirectMessages: 'chat/getMoreDirectMessages'
    }),
    bargeinPrompt: function (call) {
      this.bargeinCall = call
      this.showBargeinPrompt = true
    },
    buddyExten: function () {
      if (this.hasParkedCalls) {
        return this.parkedSlot
      }
      return this.buddy.exten
    },
    callBuddy: function () {
      this.callNumber({ number: this.buddyExten() })
    },
    async startChat () {
      this.startChatLoading = true
      if (!this.currentUser) {
        await this.chatAuth()
      }
      const response = await this.createDMRoom(this.buddyChatUsername)
      const roomId = response?.data?.room?.rid
      const dm = this.directConversations.some(dm => dm.id === roomId)
      if (!dm) {
        await this.getDirectMessages()
        const dm = this.directConversations.some(dm => dm.id === roomId)
        if (!dm) {
          await this.findRoomInMoreDirectMessages(roomId)
        }
      }
      this.startChatLoading = false
      this.$router.push({ name: 'chats', params: { roomType: 'direct', roomId: roomId } })
    },
    async findRoomInMoreDirectMessages (roomId) {
      while (!this.dmLoaded) {
        await this.getMoreDirectMessages()
        const dm = this.directConversations.some(dm => dm.id === roomId)
        if (dm) {
          break
        }
      }
    }
  }
}
</script>

<style scoped>
.voicemail {cursor: default}
.chanspy-menu i {line-height: 20px}
</style>
